/**
 * Spotify Web Playback SDK Hook
 * Real playback control with Spotify Premium
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { spotifyTokenCache } from '@/lib/spotify-token-cache';
import { toast } from 'sonner';

interface SpotifyPlayer {
  connect: () => Promise<boolean>;
  disconnect: () => void;
  addListener: (event: string, callback: (state: any) => void) => void;
  removeListener: (event: string) => void;
  getCurrentState: () => Promise<any>;
  togglePlay: () => Promise<void>;
  nextTrack: () => Promise<void>;
  previousTrack: () => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  seek: (position_ms: number) => Promise<void>;
  setVolume: (volume: number) => Promise<void>;
}

declare global {
  interface Window {
    onSpotifyWebPlaybackSDKReady: () => void;
    Spotify: {
      Player: new (options: any) => SpotifyPlayer;
    };
  }
}

interface UseSpotifyPlaybackReturn {
  player: SpotifyPlayer | null;
  deviceId: string | null;
  isReady: boolean;
  isPlaying: boolean;
  currentTrack: any;
  position: number;
  duration: number;
  play: (uri: string) => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  next: () => Promise<void>;
  previous: () => Promise<void>;
  seek: (position_ms: number) => Promise<void>;
  setVolume: (volume: number) => Promise<void>;
}

export const useSpotifyPlayback = (): UseSpotifyPlaybackReturn => {
  const [player, setPlayer] = useState<SpotifyPlayer | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState<any>(null);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const accessTokenRef = useRef<string | null>(null);

  // Load Spotify SDK
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    document.body.appendChild(script);

    window.onSpotifyWebPlaybackSDKReady = async () => {
      await initializePlayer();
    };

    return () => {
      if (player) {
        player.disconnect();
      }
    };
  }, []);

  const getAccessToken = async (): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // ✅ 1. Verificar cache local primeiro
      const cachedToken = await spotifyTokenCache.getToken(user.id);
      if (cachedToken) {
        logger.debug('Using cached Spotify access token', { userId: user.id });
        return cachedToken.accessToken;
      }

      logger.debug('No cached token, fetching from Supabase', { userId: user.id });

      // ✅ 2. Buscar do Supabase se não houver cache
      const { data, error } = await supabase
        .from('music_connections')
        .select('access_token, expires_at, device_id, metadata')
        .eq('user_id', user.id)
        .eq('provider', 'spotify')
        .maybeSingle();

      if (error) {
        logger.error('Failed to get Spotify token', error);
        return null;
      }

      if (!data) {
        return null;
      }

      // Check if token expired
      if (new Date(data.expires_at) < new Date()) {
        // Refresh token
        const { data: refreshData } = await supabase.functions.invoke('refresh-music-token', {
          body: { provider: 'spotify' }
        });

        if (refreshData?.access_token) {
          // ✅ 3. Atualizar cache com novo token
          await spotifyTokenCache.updateAccessToken(
            user.id,
            refreshData.access_token,
            refreshData.expires_at
          );
          return refreshData.access_token;
        }

        return null;
      }

      // ✅ 4. Salvar no cache para próximas chamadas
      await spotifyTokenCache.setToken({
        userId: user.id,
        accessToken: data.access_token,
        expiresAt: data.expires_at,
        deviceId: data.device_id || undefined,
        metadata: data.metadata as Record<string, any> | undefined
      });

      return data.access_token;
    } catch (error) {
      logger.error('Error getting Spotify access token', error);
      return null;
    }
  };

  const initializePlayer = async () => {
    const token = await getAccessToken();
    if (!token) {
      toast.error('Conecte sua conta Spotify primeiro');
      return;
    }

    accessTokenRef.current = token;

    const spotifyPlayer = new window.Spotify.Player({
      name: 'You Better Run',
      getOAuthToken: (cb: (token: string) => void) => {
        cb(token);
      },
      volume: 0.8,
    });

    // Ready
    spotifyPlayer.addListener('ready', ({ device_id }: any) => {
      logger.info('Spotify Player Ready', { device_id });
      setDeviceId(device_id);
      setIsReady(true);
      toast.success('Player Spotify conectado!');
    });

    // Not Ready
    spotifyPlayer.addListener('not_ready', ({ device_id }: any) => {
      logger.warn('Spotify Player Not Ready', { device_id });
      setIsReady(false);
    });

    // Player state changed
    spotifyPlayer.addListener('player_state_changed', (state: any) => {
      if (!state) return;

      setCurrentTrack(state.track_window.current_track);
      setIsPlaying(!state.paused);
      setPosition(state.position);
      setDuration(state.duration);
    });

    // Error handling
    spotifyPlayer.addListener('initialization_error', ({ message }: any) => {
      logger.error('Spotify initialization error', { message });
      toast.error('Erro ao inicializar player');
    });

    spotifyPlayer.addListener('authentication_error', ({ message }: any) => {
      logger.error('Spotify authentication error', { message });
      toast.error('Erro de autenticação Spotify');
    });

    spotifyPlayer.addListener('account_error', ({ message }: any) => {
      logger.error('Spotify account error', { message });
      toast.error('Spotify Premium necessário');
    });

    spotifyPlayer.addListener('playback_error', ({ message }: any) => {
      logger.error('Spotify playback error', { message });
      toast.error('Erro ao reproduzir música');
    });

    await spotifyPlayer.connect();
    setPlayer(spotifyPlayer);
  };

  const play = async (uri: string) => {
    if (!deviceId || !accessTokenRef.current) {
      toast.error('Player não está pronto');
      return;
    }

    try {
      await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessTokenRef.current}`,
        },
        body: JSON.stringify({
          uris: [uri],
        }),
      });
    } catch (error) {
      logger.error('Error playing track', error);
      toast.error('Erro ao tocar música');
    }
  };

  const pause = async () => {
    if (player) {
      await player.pause();
    }
  };

  const resume = async () => {
    if (player) {
      await player.resume();
    }
  };

  const next = async () => {
    if (player) {
      await player.nextTrack();
    }
  };

  const previous = async () => {
    if (player) {
      await player.previousTrack();
    }
  };

  const seek = async (position_ms: number) => {
    if (player) {
      await player.seek(position_ms);
    }
  };

  const setVolume = async (volume: number) => {
    if (player) {
      await player.setVolume(volume);
    }
  };

  return {
    player,
    deviceId,
    isReady,
    isPlaying,
    currentTrack,
    position,
    duration,
    play,
    pause,
    resume,
    next,
    previous,
    seek,
    setVolume,
  };
};
